import React, { useState } from 'react';
import './RetrieveLicense.css';

function RetrieveLicense() {
    const [email, setEmail] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const apiUrl = process.env.REACT_APP_API_URL || '/api';
            const endpoint = apiUrl.endsWith('/api') ? `${apiUrl}/retrieve-license` : `${apiUrl}/retrieve-license.php`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email.trim(),
                    customer_id: customerId.trim(),
                }),
            });

            const data = await response.json();

            if (data.error) {
                setError(data.message || data.error);
            } else {
                setResult(data);
            }
        } catch (err) {
            setError('Failed to retrieve license. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const downloadLicense = () => {
        if (!result || !result.license_key) return;

        const blob = new Blob([result.license_key], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'batchmark-pdf-license.key';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const copyToClipboard = () => {
        if (!result || !result.license_key) return;

        navigator.clipboard.writeText(result.license_key);
        alert('License key copied to clipboard!');
    };

    return (
        <div className="retrieve-page">
            <div className="retrieve-container">
                <button className="back-button" onClick={() => window.location.href = '/'}>
                    ← Back to Home
                </button>

                <div className="header">
                    <h1>🔑 Retrieve Your License</h1>
                    <p className="subtitle">
                        Enter your email and customer ID to retrieve your BatchMark PDF license key
                    </p>
                </div>

                {!result && (
                    <form onSubmit={handleSubmit} className="retrieve-form">
                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                            />
                            <small>The email address you used during purchase</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="customerId">Customer ID</label>
                            <input
                                type="text"
                                id="customerId"
                                value={customerId}
                                onChange={(e) => setCustomerId(e.target.value)}
                                placeholder="cus_xxxxxxxxxxxxx"
                                required
                            />
                            <small>Found in your purchase confirmation email</small>
                        </div>

                        {error && (
                            <div className="error-message">
                                <strong>❌ {error}</strong>
                                <p>Please check your details and try again, or contact support at support@batchmarkpdf.com</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="submit-button"
                            disabled={loading}
                        >
                            {loading ? 'Retrieving...' : 'Retrieve License'}
                        </button>
                    </form>
                )}

                {result && (
                    <div className="result-section">
                        <div className="success-icon">✅</div>
                        <h2>License Found!</h2>

                        <div className="license-details">
                            <div className="detail-row">
                                <span className="label">Email:</span>
                                <span className="value">{result.email}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Customer ID:</span>
                                <span className="value">{result.customer_id}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Order ID:</span>
                                <span className="value">{result.order_id}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Created:</span>
                                <span className="value">{new Date(result.created_at).toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="license-section">
                            <h3>Your License Key</h3>
                            <div className="license-key-box">
                                <code>{result.license_key}</code>
                            </div>
                            <div className="action-buttons">
                                <button className="primary-button" onClick={downloadLicense}>
                                    📥 Download License File
                                </button>
                                <button className="secondary-button" onClick={copyToClipboard}>
                                    📋 Copy to Clipboard
                                </button>
                            </div>
                        </div>

                        <button
                            className="link-button"
                            onClick={() => {
                                setResult(null);
                                setEmail('');
                                setCustomerId('');
                            }}
                        >
                            Retrieve Another License
                        </button>
                    </div>
                )}

                <div className="help-section">
                    <h3>Need Help?</h3>
                    <ul>
                        <li><strong>Where's my Customer ID?</strong> Check your purchase confirmation email</li>
                        <li><strong>Can't find your email?</strong> Check your spam folder</li>
                        <li><strong>Still having issues?</strong> Contact us at <a href="mailto:support@batchmarkpdf.com">support@batchmarkpdf.com</a></li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default RetrieveLicense;
